import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import axiosInstance from "../../utils/axiosInstance";
import FormModal from "./FormModal";
import PhotoViewerModal from "../PhotoViewerModal";

const TYPE_LABELS = {
  deviation: "Deviation",
  missing_task: "Missing task",
  ata: "ÄTA",
};

const personName = (person) =>
  person ? [person.firstname, person.lastname].filter(Boolean).join(" ") : "Unknown";

const RESOLVE_MODES = [
  {
    id: "create",
    label: "Create new task",
    hint: "Adds a new task to this project's task list.",
    action: "Create task and move hours",
  },
  {
    id: "existing",
    label: "Use existing task",
    hint: "Moves the hours onto a task the project already has.",
    action: "Attach task and move hours",
  },
];

/**
 * Admin detail view for one report flag.
 *
 * For a missing task this is also where the real work happens: the admin creates the
 * task (or points at an existing one) and the worker's hours move onto it in one action.
 */
export default function FlagDetailModal({ flagId, isOpen, onClose, onChanged }) {
  const [flag, setFlag] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);

  // Missing-task resolution form
  const [bundleTasks, setBundleTasks] = useState([]);
  const [resolveMode, setResolveMode] = useState("create"); // "create" | "existing"
  const [taskName, setTaskName] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [saveToRegistry, setSaveToRegistry] = useState(false);
  const [hours, setHours] = useState("");
  const [existingTaskId, setExistingTaskId] = useState("");
  const [existingSubtaskId, setExistingSubtaskId] = useState("");

  const activeMode = RESOLVE_MODES.find((mode) => mode.id === resolveMode) || RESOLVE_MODES[0];
  const selectedExistingTask = bundleTasks.find(
    (task) => String(task._id) === String(existingTaskId)
  );
  const selectedSubtasks = selectedExistingTask?.subtasks || [];

  const loadFlag = useCallback(async () => {
    if (!flagId) return;
    setLoading(true);
    setError("");
    try {
      const resp = await axiosInstance.get(`/admin/report-flags/${flagId}`);
      setFlag(resp.data.flag);
      setReport(resp.data.report);
      setTaskName(resp.data.flag?.proposedTaskName || "");
      setHours(
        resp.data.flag?.proposedHours != null ? String(resp.data.flag.proposedHours) : ""
      );

      if (resp.data.flag?.type === "missing_task") {
        const tasksResp = await axiosInstance.get(
          `/admin/report-flags/${flagId}/bundle-tasks`
        );
        setBundleTasks(tasksResp.data.tasks || []);
      } else {
        setBundleTasks([]);
      }
    } catch (err) {
      console.error("Error loading flag", err);
      setError(err.response?.data?.message || "Could not load this item");
    } finally {
      setLoading(false);
    }
  }, [flagId]);

  useEffect(() => {
    if (isOpen) loadFlag();
    else {
      setFlag(null);
      setReport(null);
      setComment("");
      setError("");
      setResolveMode("create");
      setExistingTaskId("");
      setExistingSubtaskId("");
    }
  }, [isOpen, loadFlag]);

  const patchFlag = async (body) => {
    setSaving(true);
    setError("");
    try {
      const resp = await axiosInstance.patch(`/admin/report-flags/${flagId}`, body);
      setFlag(resp.data.flag);
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    try {
      const resp = await axiosInstance.post(`/admin/report-flags/${flagId}/comments`, {
        text: comment.trim(),
      });
      setFlag(resp.data.flag);
      setComment("");
    } catch (err) {
      setError(err.response?.data?.message || "Could not add comment");
    } finally {
      setSaving(false);
    }
  };

  const handleResolveMissingTask = async () => {
    setSaving(true);
    setError("");
    try {
      const body =
        resolveMode === "existing"
          ? {
              existingTaskId,
              existingSubtaskId: existingSubtaskId || undefined,
              hours: hours || undefined,
            }
          : {
              taskName,
              description: taskDescription || undefined,
              estimatedHours: estimatedHours || undefined,
              saveToRegistry,
              hours: hours || undefined,
            };
      const resp = await axiosInstance.post(
        `/admin/report-flags/${flagId}/resolve-missing-task`,
        body
      );
      setFlag(resp.data.flag);
      setReport(resp.data.report);
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || "Could not resolve this item");
    } finally {
      setSaving(false);
    }
  };

  const isResolved = flag?.status === "resolved";
  const reportLink = flag
    ? `/report?userId=${flag.createdBy?._id || flag.createdBy}&month=${dayjs(flag.date).format(
        "YYYY-MM"
      )}&view=reports&date=${dayjs(flag.date).format("YYYY-MM-DD")}`
    : "#";

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={flag ? TYPE_LABELS[flag.type] || "Follow-up" : "Follow-up"}
        size="xl"
      >
        {loading && <p className="text-gray-500">Loading...</p>}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {flag && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  isResolved ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                }`}
              >
                {isResolved ? "Resolved" : "Open"}
              </span>
              <span className="text-gray-500">
                {dayjs(flag.date).format("YYYY-MM-DD")} · {personName(flag.createdBy)} ·{" "}
                {flag.project?.name}
              </span>
            </div>

            {flag.type === "missing_task" && (
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">Worker proposed</div>
                <div className="text-sm font-medium text-gray-900">{flag.proposedTaskName}</div>
                {flag.proposedHours != null && (
                  <div className="text-sm text-gray-600">{flag.proposedHours} hours logged</div>
                )}
              </div>
            )}

            {flag.description && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Description</div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{flag.description}</p>
              </div>
            )}

            {flag.hoursImpact != null && (
              <div className="text-sm text-gray-700">
                Estimated impact: <span className="font-medium">{flag.hoursImpact} hours</span>
              </div>
            )}

            {flag.photos?.length > 0 && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Photos</div>
                <div className="flex flex-wrap gap-2">
                  {flag.photos.map((url) => (
                    <button
                      key={url}
                      type="button"
                      onClick={() => setPhotoViewerOpen(true)}
                      className="w-20 h-20 rounded overflow-hidden border border-gray-200"
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 text-sm">
              <a
                href={reportLink}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Open the worker's time report
              </a>
              {report && <span className="text-gray-500">Day total: {report.hours} hours</span>}
            </div>

            {/* Missing-task resolution */}
            {flag.type === "missing_task" && !isResolved && (
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="font-medium text-gray-900">Resolve</h3>

                <div
                  role="radiogroup"
                  aria-label="How to resolve this missing task"
                  className="flex p-1 bg-gray-100 rounded-full"
                >
                  {RESOLVE_MODES.map((mode) => {
                    const selected = resolveMode === mode.id;
                    return (
                      <button
                        key={mode.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => {
                          setResolveMode(mode.id);
                          setExistingSubtaskId("");
                        }}
                        className={`flex-1 px-4 py-2 rounded-full text-sm transition-colors ${
                          selected
                            ? "bg-white text-blue-700 font-medium shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {mode.label}
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                  <p className="text-sm text-gray-500">{activeMode.hint}</p>

                  {resolveMode === "create" ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Task name</label>
                        <input
                          type="text"
                          value={taskName}
                          onChange={(e) => setTaskName(e.target.value)}
                          className="w-full px-3 py-2 border rounded text-sm bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Description (optional)
                        </label>
                        <textarea
                          value={taskDescription}
                          onChange={(e) => setTaskDescription(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border rounded text-sm bg-white"
                        />
                      </div>
                      <div className="flex items-end gap-3">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Estimated hours
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={estimatedHours}
                            onChange={(e) => setEstimatedHours(e.target.value)}
                            className="w-24 px-3 py-2 border rounded text-sm bg-white"
                          />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                          <input
                            type="checkbox"
                            checked={saveToRegistry}
                            onChange={(e) => setSaveToRegistry(e.target.checked)}
                          />
                          Save to task registry
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Existing task</label>
                        <select
                          value={existingTaskId}
                          onChange={(e) => {
                            setExistingTaskId(e.target.value);
                            setExistingSubtaskId("");
                          }}
                          className="w-full px-3 py-2 border rounded text-sm bg-white"
                        >
                          <option value="">Select a task...</option>
                          {bundleTasks.map((task) => (
                            <option key={task._id} value={task._id}>
                              {task.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      {existingTaskId && (
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Subtask (optional)
                          </label>
                          <select
                            value={existingSubtaskId}
                            onChange={(e) => setExistingSubtaskId(e.target.value)}
                            className="w-full px-3 py-2 border rounded text-sm bg-white"
                          >
                            <option value="">None — hours on the task itself</option>
                            {selectedSubtasks.map((subtask) => (
                              <option key={subtask._id} value={subtask._id}>
                                {subtask.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Hours to move onto the task
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="w-24 px-3 py-2 border rounded text-sm"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleResolveMissingTask}
                  disabled={
                    saving ||
                    (resolveMode === "create" ? !taskName.trim() : !existingTaskId)
                  }
                  className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                >
                  {saving ? "Saving..." : activeMode.action}
                </button>
              </div>
            )}

            {flag.createdTask?.taskId && (
              <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                Hours were moved onto the task.
              </div>
            )}

            {/* Comments */}
            <div>
              <div className="text-xs text-gray-500 mb-2">Internal notes</div>
              <div className="space-y-2 mb-2">
                {(flag.comments || []).map((entry) => (
                  <div key={entry._id} className="p-2 bg-gray-50 rounded text-sm">
                    <div className="text-xs text-gray-500">
                      {personName(entry.userId)} · {dayjs(entry.createdAt).format("YYYY-MM-DD HH:mm")}
                    </div>
                    <div className="text-gray-800 whitespace-pre-wrap">{entry.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 px-3 py-2 border rounded text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={saving || !comment.trim()}
                  className="px-3 py-2 border rounded text-sm disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Status actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {isResolved ? (
                <button
                  type="button"
                  onClick={() => patchFlag({ status: "open" })}
                  disabled={saving}
                  className="px-4 py-2 border rounded text-sm disabled:opacity-50"
                >
                  Reopen
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => patchFlag({ status: "resolved" })}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  Mark resolved
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded text-sm ml-auto"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </FormModal>

      <PhotoViewerModal
        isOpen={photoViewerOpen}
        onClose={() => setPhotoViewerOpen(false)}
        photos={flag?.photos || []}
        reportDate={flag ? dayjs(flag.date).format("YYYY-MM-DD") : ""}
        projectName={flag?.project?.name || ""}
        projectAddress=""
      />
    </>
  );
}
